/**
 * Parse settingsTemplate structure back to analyticsFileData structure
 * expected by populateAjsBuilderForm
 *
 * This is the inverse operation of buildSettingsTemplate - it flattens the nested
 * settingsTemplate structure from YAML back into the flat analyticsFileData format
 * used by the form
 *
 * @param {object} settingsTemplate - The settings template from YAML
 * @returns {object} analyticsFileData structure
 */
export default function parseSettingsTemplate(settingsTemplate) {
  const { syncInjectorSettings, integrations, ...settings } = settingsTemplate;

  // Transform syncs from settingsTemplate format to analyticsFileData format
  const identitySyncs = (syncInjectorSettings.syncs || []).map(sync => ({
    name: sync.name,
    settings: sync.settings || {},
    consentCategoryId: sync.consentCategoryId,
    tcfEnabled: sync.tcfEnabled,
  }));

  // Transform integrations from settingsTemplate format to analyticsFileData format
  const transformedIntegrations = (integrations || []).map(integration => ({
    name: integration.name,
    options: integration.options || {},
  }));

  // Extract individual settings from syncInjectorSettings
  const analyticsFileData = {
    settings: {
      ...settings,
      impliedConsent: syncInjectorSettings.privacy?.impliedConsent,
      cookieName: syncInjectorSettings.privacy?.cookieName,
      itpSettings: syncInjectorSettings.itpSettings?.enabled,
      windowVariableName: settings.windowVariableName,
      storagePrefix: syncInjectorSettings.storage?.marker,
      storageType: syncInjectorSettings.storage?.type,
      storageDatabase: syncInjectorSettings.storage?.database,
      botFilterEnabled: syncInjectorSettings.botFilteringSettings?.enabled,
      matchIDConsentCode: syncInjectorSettings.consent?.matchIDConsentCodeID,
      consentSource: syncInjectorSettings.consent?.consentSource,
      optInValues: syncInjectorSettings.consent?.optInValues,
      globalPrivacyControl: syncInjectorSettings.consent?.globalPrivacyControl,
    },
    identitySyncs,
    integrations: transformedIntegrations,
  };

  // Add google tag settings if present
  if (syncInjectorSettings.googleGtagSettings) {
    analyticsFileData.google_tag_settings = {
      tagId: syncInjectorSettings.googleGtagSettings.parentTagID,
      consentEnabled: syncInjectorSettings.googleGtagSettings.consentModeEnabled,
      consentExplicitConsent: syncInjectorSettings.googleGtagSettings.explicitConsent,
      consentSyncCategories: syncInjectorSettings.googleGtagSettings.consentSyncCategories,
      refreshOnEmptyHash: syncInjectorSettings.googleGtagSettings.refreshOnEmptyHash ?? false,
      vtcEnabled: syncInjectorSettings.googleGtagSettings.vtcEnabled ?? false,
      gtgEnabled: syncInjectorSettings.googleGtagSettings.gtgEnabled ?? false,
      gtgScriptSource: syncInjectorSettings.googleGtagSettings.gtgScriptSource ?? null,
      // Omitted when absent so legacy templates parse byte-identically
      ...(syncInjectorSettings.googleGtagSettings.egsm && { egsm: syncInjectorSettings.googleGtagSettings.egsm }),
    };
  }

  return analyticsFileData;
}
